import { useSyncExternalStore } from "react"
import type { i18n as I18n, TFunction } from "i18next"
import { useOptionalLocalization } from "@florisoft/framework"
import { getDefaultUiLocalization } from "./default-ui-localization"

interface UiTranslation {
  readonly t: TFunction<"florisoft-ui">
}

interface UiTranslationSource {
  readonly subscribe: (onChange: () => void) => () => void
  readonly current: () => UiTranslation
}

/**
 * One subscription per i18n instance, shared by every component. Components such as grid cells mount by the hundred
 * while a list scrolls, so a translation must cost a context read, not a subscription of its own.
 */
const sources = new WeakMap<I18n, UiTranslationSource>()

function sourceFor(i18n: I18n): UiTranslationSource {
  let source = sources.get(i18n)
  if (source !== undefined) return source
  let translation: UiTranslation | undefined
  let language: string | undefined
  source = {
    subscribe(onChange) {
      const changed = () => onChange()
      i18n.on("languageChanged", changed)
      return () => i18n.off("languageChanged", changed)
    },
    current() {
      if (translation === undefined || language !== i18n.language) {
        language = i18n.language
        translation = { t: i18n.getFixedT(null, "florisoft-ui") as TFunction<"florisoft-ui"> }
      }
      return translation
    },
  }
  sources.set(i18n, source)
  return source
}

export function useUiTranslation(): UiTranslation {
  const ambient = useOptionalLocalization()
  const ambientNamespaces = ambient?.i18n.options.ns
  const ambientHasUiNamespace =
    typeof ambientNamespaces === "string"
      ? ambientNamespaces === "florisoft-ui"
      : Array.isArray(ambientNamespaces)
        ? ambientNamespaces.includes("florisoft-ui")
        : ambient?.i18n.hasResourceBundle("en", "florisoft-ui") === true
  if (ambient && !ambientHasUiNamespace) {
    throw new Error("@florisoft/ui requires florisoftUiResources in the ambient application localization resources.")
  }
  const source = sourceFor(ambient?.i18n ?? getDefaultUiLocalization().i18n)
  return useSyncExternalStore(source.subscribe, source.current, source.current)
}
